import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { MAX_TRANG_MOI_LAN_XUAT } from 'shared';
import type {
  RegistryFilter,
  RegistryPage as RegistryPageData,
  RegistryRow,
  RegistryScope,
  RegistrySort,
} from 'shared';
import { api } from '../api';
import { ScanProgressPanel } from '../components/ScanProgressPanel';
import { useAuth } from '../auth';
import { useDebounced } from '../useDebounced';
import { AddPageDialog } from '../components/AddPageDialog';
import { RowActions } from '../components/RowActions';
import { ScoreMeter } from '../components/ScoreMeter';
import { StatStrip } from '../components/StatStrip';
import { formatDateTime, formatRelative, isStale, prodColor, prodInitials } from '../format';
import { DISPLAY, MONO, theme, tokens } from '../theme';

/**
 * Khoá sắp xếp và bộ lọc giờ do MÁY CHỦ hiểu (spec SC-13 §2.3), nên kiểu lấy thẳng
 * từ `shared` thay vì khai lại ở đây — khai lại là để hai bên lệch nhau.
 */
type SortKey = RegistrySort;
type Filter = RegistryFilter;

/** Dưới mốc này là trang cần chú ý — trùng ngưỡng đổi màu của thanh điểm. */
const ATTENTION = 75;

/** Ô chữ viết tắt + tên + slug, như cột "Sản phẩm" của demo. */
function ProductCell({ row }: { row: RegistryRow }) {
  return (
    <Stack direction="row" spacing={1.4} alignItems="center">
      <Box
        sx={{
          width: 32,
          height: 32,
          flex: 'none',
          borderRadius: '8px',
          bgcolor: prodColor(row.slug),
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontFamily: DISPLAY,
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        {prodInitials(row.code)}
      </Box>
      {/* Cắt bằng ellipsis chứ không cho xuống dòng: slug dài như
          `smart-factory-iot` sẽ làm dòng đó cao hơn các dòng khác. */}
      <Box minWidth={0} sx={{ '& > *': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14.5, lineHeight: 1.35 }}>{row.code}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary' }}>
          /{row.slug}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * Số mục chưa đạt, kèm số mục nghiêm trọng có NHÃN.
 *
 * Bản cũ để hai con số cạnh nhau không nhãn ("10" và "3") nên phải trỏ chuột mới
 * biết con số thứ hai nghĩa là gì.
 */
function ProblemCell({ total, bad }: { total: number | null; bad: number | null }) {
  if (total === null) return <Typography sx={{ color: 'text.disabled' }}>—</Typography>;
  if (total === 0) {
    return <Chip size="small" label="đạt hết" sx={{ bgcolor: tokens.okBg, color: tokens.okText }} />;
  }
  return (
    <Tooltip
      title={
        bad
          ? `${total} mục chưa đạt, trong đó ${bad} mục nghiêm trọng`
          : `${total} mục chưa đạt, không có mục nào nghiêm trọng`
      }
    >
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Box sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, width: 20, textAlign: 'right' }}>
          {total}
        </Box>
        {/* Thanh hai đoạn: đoạn đỏ là phần "nặng" nằm TRONG tổng, không phải một
            đại lượng riêng. Đây là lý do không tách thành cột thứ hai — tách ra
            thì mắt đọc hai con số như hai phép đo cộng được với nhau. */}
        <Box sx={{ width: 34, height: 6, borderRadius: 999, bgcolor: tokens.amber, display: 'flex', overflow: 'hidden', flex: 'none' }}>
          <Box sx={{ width: `${((bad ?? 0) / total) * 100}%`, bgcolor: tokens.rose }} />
        </Box>
        <Box sx={{ fontSize: 12.5, color: bad ? tokens.roseText : 'text.disabled', width: 44, whiteSpace: 'nowrap' }}>
          {bad ? `${bad} nặng` : '—'}
        </Box>
      </Stack>
    </Tooltip>
  );
}

function H1Cell({ count }: { count: number | null }) {
  if (count === null) return <Typography sx={{ color: 'text.disabled' }}>—</Typography>;
  const ok = count === 1;
  return (
    <Tooltip title={ok ? 'Đúng một thẻ H1' : `${count} thẻ H1 — nên chỉ có một`}>
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: 13,
          fontWeight: 600,
          px: 1,
          py: 0.3,
          borderRadius: 999,
          bgcolor: ok ? tokens.line2 : tokens.amberBg,
          color: ok ? tokens.muted : tokens.amberText,
        }}
      >
        {count}
      </Box>
    </Tooltip>
  );
}

/**
 * Bốn ô số liệu ở đầu màn.
 *
 * Số do MÁY CHỦ tính trên toàn bộ phạm vi, không tính từ mảng đang hiển thị: sau
 * khi phân trang thì mảng đó chỉ có 25 dòng, và bốn con số này phải nói về cả danh
 * sách chứ không phải về trang đang xem.
 */
function RegistryStats({ tongKet }: { tongKet: RegistryPageData['tongKet'] }) {
  return (
    <Box sx={{ mb: 3 }}>
      <StatStrip
        items={[
          { value: String(tongKet.tongSoTrang), label: 'Trang trong Registry' },
          {
            value: tongKet.diemTrungBinh === null ? '—' : String(tongKet.diemTrungBinh),
            label: tongKet.boQuaVìDocHut
              ? `Điểm SEO trung bình (bỏ ${tongKet.boQuaVìDocHut} trang đọc hụt)`
              : 'Điểm SEO trung bình',
          },
          {
            value: String(tongKet.canChuY),
            label: `Trang cần chú ý (dưới ${ATTENTION})`,
            color: tongKet.canChuY ? tokens.roseText : tokens.okText,
          },
          { value: String(tongKet.tongMucChuaDat), label: 'Mục chưa đạt, toàn bộ site' },
        ]}
      />
    </Box>
  );
}

/** Dạng thẻ cho màn hình hẹp — bảng 8 cột không thể đọc trên điện thoại. */
function RowCard({
  row,
  onOpen,
  checked,
  onToggle,
}: {
  row: RegistryRow;
  onOpen: () => void;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{ p: 1.8, cursor: 'pointer', '&:hover': { borderColor: tokens.teal } }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
          <Checkbox
            size="small"
            checked={checked}
            // Bấm ô chọn KHÔNG được mở trang chi tiết — cả thẻ là một nút bấm.
            onClick={(e) => e.stopPropagation()}
            onChange={onToggle}
            inputProps={{ 'aria-label': `Chọn trang ${row.code}` }}
            sx={{ ml: '-9px' }}
          />
          <ProductCell row={row} />
        </Stack>
        <ScoreMeter score={row.seoScore} prev={row.prevScore} />
      </Stack>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1.2 }}>
        {row.title ?? '—'}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" mt={1.2}>
        <ProblemCell total={row.problemCount} bad={row.badCount} />
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
          {formatRelative(row.lastScannedAt)}
        </Typography>
      </Stack>
    </Paper>
  );
}

export function RegistryPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useAuth();
  // Ô chọn phạm vi CHỈ hiện với Web Administrator (spec SC-03 §5) — vai trò khác
  // gửi `scope=all` cũng bị máy chủ bỏ qua, nên hiện ra chỉ gây hiểu nhầm.
  const laAdmin = user?.role === 'WEB_ADMINISTRATOR';

  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [scope, setScope] = useState<RegistryScope>('mine');
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'seoScore', asc: true });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Chờ ô tìm đứng yên 300 ms rồi mới gọi API. Gõ "rooland" mà gọi theo từng phím
  // là bảy lượt gọi cho một lần tìm, và phản hồi có thể về không đúng thứ tự gõ.
  const qCham = useDebounced(q.trim());

  const truyVan = {
    page,
    pageSize,
    scope,
    filter,
    sort: sort.key,
    asc: sort.asc,
    ...(qCham ? { q: qCham } : {}),
  };

  const { data, isLoading, error } = useQuery({
    // Mọi tham số nằm trong khoá truy vấn: đổi bất kỳ cái nào là một tập dữ liệu
    // khác, gộp chung khoá thì bảng hiện dữ liệu cũ cho tới khi tải xong.
    queryKey: ['registry', truyVan],
    queryFn: () => api.listRegistry(truyVan),
    // Giữ dữ liệu trang trước trong lúc tải trang mới — không có nó thì mỗi lần
    // bấm sang trang bảng chớp trắng một nhịp.
    placeholderData: (truoc) => truoc,
  });

  /**
   * Lượt vừa bấm. Chưa bấm gì thì để `undefined` — khi đó thanh tiến độ tự lấy
   * lượt GẦN NHẤT, kể cả lượt định kỳ do hệ thống chạy đêm qua.
   */
  const [batchId, setBatchId] = useState<string | undefined>(undefined);

  /**
   * Quét cả danh sách — từ spec SC-08 §4.3 chỉ XẾP HÀNG rồi trả về ngay.
   *
   * `isPending` giờ chỉ kéo dài vài chục mili giây (thời gian xếp hàng), nên nút
   * không còn là nơi hiện tiến độ nữa. Tiến độ nằm ở `ScanProgressPanel` bên
   * dưới, và nó sống độc lập với lần bấm này — mở lại trang vẫn thấy.
   */
  const scanAll = useMutation({
    mutationFn: () => api.scanAll(scope),
    onSuccess: (p) => {
      setBatchId(p.id);
      void qc.invalidateQueries({ queryKey: ['quet', 'tien-do'] });
    },
  });

  const rows = data?.rows ?? [];

  /**
   * Trang được tick để xuất prompt gộp — spec SC-20 §3.2.
   *
   * Giữ theo `slug` chứ không theo chỉ số dòng, và **không xoá khi sang trang
   * khác**: chọn 5 trang ở trang 1 rồi lật sang trang 2 chọn thêm 3 là việc bình
   * thường. Đổi lại, số đã chọn phải hiện rõ ở một dải riêng, nếu không người
   * dùng quên mất là mình còn tick gì ở trang trước.
   */
  const [daChon, setDaChon] = useState<Set<string>>(new Set());
  const soChon = daChon.size;
  const quaTran = soChon > MAX_TRANG_MOI_LAN_XUAT;

  function tick(slug: string) {
    setDaChon((cu) => {
      const moi = new Set(cu);
      if (moi.has(slug)) moi.delete(slug);
      else moi.add(slug);
      return moi;
    });
  }

  /** Ô chọn ở hàng tiêu đề chỉ tác động lên các dòng ĐANG HIỆN, không lên cả danh sách. */
  const chonHetTrangNay = rows.length > 0 && rows.every((r) => daChon.has(r.slug));
  function tickCaTrang() {
    setDaChon((cu) => {
      const moi = new Set(cu);
      for (const r of rows) {
        if (chonHetTrangNay) moi.delete(r.slug);
        else moi.add(r.slug);
      }
      return moi;
    });
  }

  const taiPrompt = useMutation({
    mutationFn: () => api.exportPrompt([...daChon]),
  });

  /** Đổi bộ lọc/tìm/sắp xếp thì phải về trang 1 — nếu không sẽ rơi vào trang trống. */
  function doiVaVeTrangDau(fn: () => void) {
    fn();
    setPage(1);
  }

  function head(
    key: SortKey,
    label: string,
    align: 'left' | 'right' | 'center' = 'left',
    width?: number,
    hint?: string,
  ) {
    const active = sort.key === key;
    const dir = sort.asc ? 'asc' : 'desc';
    const cell = (
      <TableCell align={align} sortDirection={active ? dir : false} sx={{ width }}>
        <TableSortLabel
          active={active}
          direction={dir}
          onClick={() =>
            doiVaVeTrangDau(() => setSort({ key, asc: active ? !sort.asc : key === 'code' }))
          }
          sx={{
            color: '#fff !important',
            '& .MuiTableSortLabel-icon': { color: `${tokens.mint} !important` },
          }}
        >
          {hint ? (
            <Tooltip title={hint}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          )}
        </TableSortLabel>
      </TableCell>
    );
    return cell;
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}>
        PRODUCT PAGE REGISTRY
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'flex-end' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 0.5, mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontSize: 28, color: tokens.ink }}>
            Trang giới thiệu sản phẩm
          </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: '70ch', mt: 0.5 }}>
            Copilot đọc trang qua HTTP, chấm điểm SEO và đề xuất nội dung chỉnh sửa. Giai đoạn 1
            chưa ghi vào mã nguồn site.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flex: 'none' }}>
          {/* "Thêm trang" là việc chính của người dùng nên để nút đầy màu; "Quét lại
              tất cả" là việc lặp lại nên hạ xuống nút viền. */}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Thêm trang
          </Button>
          <Button
            variant="outlined"
            startIcon={
              scanAll.isPending ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            disabled={scanAll.isPending}
            onClick={() => scanAll.mutate()}
          >
            {scanAll.isPending ? 'Đang xếp hàng…' : 'Quét lại tất cả'}
          </Button>
        </Stack>
      </Stack>

      <AddPageDialog open={addOpen} onClose={() => setAddOpen(false)} />

      {data && <RegistryStats tongKet={data.tongKet} />}

      {/* Tiến độ lượt quét nền — spec SC-08 §4.3.
          Thay hẳn dải "Đã quét N trang trong X giây" cũ, và cũng thay luôn lớp
          xử lý hết-giờ-ở-reverse-proxy của spec SC-09-B: lượt gọi giờ trả về
          trong vài chục mili giây nên không còn gì để hết giờ. */}
      <ScanProgressPanel id={batchId} />

      {scanAll.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(scanAll.error as Error).message}
        </Alert>
      )}

      {error && <Alert severity="error">{(error as Error).message}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1.5}
          mb={1.8}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: 16, color: tokens.ink }}>
              Danh sách trang
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
              Bấm vào một dòng để xem phân tích chi tiết
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {laAdmin && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={scope}
                onChange={(_, v: RegistryScope | null) => v && doiVaVeTrangDau(() => setScope(v))}
              >
                <ToggleButton value="mine" sx={{ fontSize: 13.5 }}>
                  Trang của tôi
                </ToggleButton>
                <ToggleButton value="all" sx={{ fontSize: 13.5 }}>
                  Tất cả
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <TextField
              size="small"
              placeholder="Tìm sản phẩm…"
              value={q}
              onChange={(e) => doiVaVeTrangDau(() => setQ(e.target.value))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                // 16px trở lên để iOS không tự phóng to khi bấm vào ô nhập.
                sx: { fontSize: 16 },
              }}
              sx={{ width: { xs: '100%', sm: 190 } }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_, v: Filter | null) => v && doiVaVeTrangDau(() => setFilter(v))}
            >
              <ToggleButton value="all" sx={{ fontSize: 13.5 }}>
                Tất cả
              </ToggleButton>
              <ToggleButton value="low" sx={{ fontSize: 13.5 }}>
                Cần chú ý
              </ToggleButton>
              <ToggleButton value="unscanned" sx={{ fontSize: 13.5 }}>
                Chưa quét
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        {/* Xem cả trang của người khác là trạng thái BẤT THƯỜNG, phải thấy được
            ngay — nếu không, admin sẽ tưởng mình đang nhìn danh sách của mình và
            quét lại cả những trang không thuộc phạm vi mình phụ trách. */}
        {scope === 'all' && (
          <Box sx={{ mb: 1.6 }}>
            <Alert severity="info" sx={{ py: 0.2 }}>
              Đang xem cả trang của người khác. Nút “Quét lại tất cả” cũng chạy trên phạm vi này.
            </Alert>
          </Box>
        )}

        {/* Dải hành động cho các trang đã tick — spec SC-20 §4.2. Chỉ hiện khi có
            ít nhất một trang được chọn: hiện thường trực thì nó chiếm chỗ và người
            dùng đọc lướt qua như một phần trang trí. */}
        {soChon > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mb: 1.6,
              px: 1.8,
              py: 1.2,
              bgcolor: tokens.okBg,
              borderColor: tokens.mint,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              spacing={1.2}
            >
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>
                  Đã chọn {soChon} trang
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: quaTran ? tokens.roseText : 'text.secondary' }}>
                  {quaTran
                    ? `Mỗi lần xuất tối đa ${MAX_TRANG_MOI_LAN_XUAT} trang — bỏ bớt ${
                        soChon - MAX_TRANG_MOI_LAN_XUAT
                      } trang.`
                    : 'Xuất một file prompt gộp, giao cho AI có mã nguồn sửa cả loạt.'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flex="none">
                <Button size="small" color="inherit" onClick={() => setDaChon(new Set())}>
                  Bỏ chọn
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    taiPrompt.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SmartToyIcon />
                    )
                  }
                  disabled={quaTran || taiPrompt.isPending}
                  onClick={() => taiPrompt.mutate()}
                >
                  Tải prompt cho AI ({soChon})
                </Button>
              </Stack>
            </Stack>
            {taiPrompt.error && (
              <Alert severity="error" sx={{ mt: 1.2, py: 0.2 }}>
                {(taiPrompt.error as Error).message}
              </Alert>
            )}
          </Paper>
        )}

        {isLoading ? (
          <Stack spacing={1}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} variant="rounded" height={44} />
            ))}
          </Stack>
        ) : compact ? (
          <Stack spacing={1.2}>
            {rows.map((row) => (
              <RowCard
                key={row.id}
                row={row}
                onOpen={() => navigate(`/pages/${row.slug}`)}
                checked={daChon.has(row.slug)}
                onToggle={() => tick(row.slug)}
              />
            ))}
          </Stack>
        ) : (
          // `overflow: visible` để chính TRANG là vùng cuộn duy nhất. Nếu để
          // TableContainer tự cuộn (mặc định của MUI), sẽ có hai vùng cuộn lồng
          // nhau và tiêu đề chỉ dính trong khung bảng chứ không dính theo trang.
          <TableContainer sx={{ overflow: 'visible' }}>
            {/* `tableLayout: fixed` là điều kiện để bảng KHÔNG BAO GIỜ tràn ra
                ngoài thẻ: bề rộng cột lấy theo khai báo ở hàng tiêu đề chứ không
                theo nội dung, nên phóng to trình duyệt hay tiêu đề trang dài đều
                chỉ làm chữ bị cắt bằng dấu "…", không đẩy bảng rộng ra. Cột
                "Tiêu đề trang" không khai bề rộng nên nhận hết phần còn lại. */}
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 42 }}>
                    <Tooltip title="Chọn các trang đang hiện để xuất prompt gộp">
                      <Checkbox
                        size="small"
                        checked={chonHetTrangNay}
                        indeterminate={!chonHetTrangNay && rows.some((r) => daChon.has(r.slug))}
                        onChange={tickCaTrang}
                        inputProps={{ 'aria-label': 'Chọn tất cả trang đang hiện' }}
                        sx={{ color: '#fff', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens.mint } }}
                      />
                    </Tooltip>
                  </TableCell>
                  {head('code', 'Sản phẩm', 'left', 215)}
                  <TableCell>Tiêu đề trang</TableCell>
                  {head('seoScore', 'Điểm SEO', 'left', 145)}
                  {head('wordCount', 'Số từ', 'right', 82)}
                  <TableCell align="center" sx={{ width: 50 }}>
                    H1
                  </TableCell>
                  {head(
                    'problemCount',
                    'Chưa đạt',
                    'left',
                    140,
                    'Số mục chưa đạt; đoạn đỏ là phần nghiêm trọng. Sắp xếp ưu tiên mục nghiêm trọng.',
                  )}
                  {head('lastScannedAt', 'Quét lúc', 'right', 112)}
                  {/* Ba biểu tượng: thao tác, mở trang thật, mũi tên vào chi tiết. */}
                  <TableCell align="right" sx={{ width: 108 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    // Cả dòng bấm được: người dùng có xu hướng bấm vào con số ở
                    // cột "Chưa đạt" chứ không phải tên sản phẩm.
                    onClick={() => navigate(`/pages/${row.slug}`)}
                    sx={{
                      cursor: 'pointer',
                      '&:nth-of-type(even) td': { bgcolor: tokens.rowAlt },
                      '&:hover td': { bgcolor: `${tokens.rowHover} !important` },
                      '&:hover .go': { color: tokens.teal },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={daChon.has(row.slug)}
                        // Không chặn nổi bọt thì tick một ô là nhảy luôn sang màn
                        // chi tiết, và người dùng mất cả danh sách đang chọn dở.
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => tick(row.slug)}
                        inputProps={{ 'aria-label': `Chọn trang ${row.code}` }}
                      />
                    </TableCell>

                    {/* Bề rộng cột do hàng tiêu đề quyết định (tableLayout fixed),
                        các ô ở đây không cần khai lại. */}
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/pages/${row.slug}`}
                        underline="none"
                        color="inherit"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ProductCell row={row} />
                      </MuiLink>
                    </TableCell>

                    <TableCell
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                      }}
                      title={row.title ?? ''}
                    >
                      {row.title ?? '—'}
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <ScoreMeter score={row.seoScore} prev={row.prevScore} />
                        {row.renderWarning && (
                          <Tooltip title="Trang có thể dựng bằng JavaScript — điểm này không đáng tin và không tính vào trung bình">
                            <Box
                              component="span"
                              sx={{
                                flex: 'none',
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: tokens.amber,
                                color: '#fff',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: 11,
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              !
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell align="right" sx={{ fontFamily: MONO, fontSize: 13.5 }}>
                      {row.wordCount?.toLocaleString('vi-VN') ?? '—'}
                    </TableCell>

                    <TableCell align="center">
                      <H1Cell count={row.h1Count} />
                    </TableCell>

                    <TableCell>
                      <ProblemCell total={row.problemCount} bad={row.badCount} />
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: 13.5,
                        color: isStale(row.lastScannedAt) ? tokens.amberText : 'text.secondary',
                      }}
                    >
                      <Tooltip title={formatDateTime(row.lastScannedAt)}>
                        <span>{formatRelative(row.lastScannedAt)}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.2} justifyContent="flex-end" alignItems="center">
                        <RowActions row={row} />
                        <Tooltip title="Mở trang thật ở tab mới">
                          <MuiLink
                            href={row.publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            // Chặn nổi bọt, nếu không dòng sẽ điều hướng sang
                            // trang chi tiết cùng lúc mở tab mới.
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: 'inline-flex', color: 'text.disabled' }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 17 }} />
                          </MuiLink>
                        </Tooltip>
                        <ChevronRightIcon className="go" sx={{ fontSize: 19, color: tokens.line }} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {data && data.tongKet.tongSoTrang > 0
                        ? 'Không có trang nào khớp bộ lọc'
                        : 'Registry chưa có trang nào'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Phân trang PHÍA SERVER (spec SC-13 §2.3). Hiện cả ở dạng thẻ trên màn
            hình hẹp — cuộn hết 25 thẻ rồi không có gì để bấm là cụt đường. */}
        {data && data.total > 0 && (
          <TablePagination
            component="div"
            count={data.total}
            page={page - 1}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => doiVaVeTrangDau(() => setPageSize(Number(e.target.value)))}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Số dòng mỗi trang"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} trong ${count}`}
          />
        )}
      </Paper>
    </Box>
  );
}
